// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import Transform from '../math/Transform';
import Joint from '../animationpack/Joint';
import Matrix4 from '../math/Matrix4';

/**
 * Joins a {@link Skeleton} with an array of {@link Joint} poses. This allows the skeleton to exist and be reused between multiple instances of poses.
 */
class SkeletonPose {

  /**
   * @param {Skeleton} skeleton
   */
  constructor(skeleton) {
    this._skeleton = skeleton;

    this._localTransforms = [];
    this._globalTransforms = [];
    this._matrixPalette = [];
    this._poseListeners = [];

    this.allocateTransforms();

    // start off in bind pose.
    this.setToBindPose();
  }

  allocateTransforms() {
    const jointCount = this._skeleton._joints.length;

    // init local transforms, global transforms, palette
    for (let i = 0; i < jointCount; i++) {
      this._localTransforms[i] = new Transform();
      this._globalTransforms[i] = new Transform();
      this._matrixPalette[i] = new Matrix4();
    }
  }

  /**
   * Update our local joint transforms so that they reflect the skeleton in bind pose.
   */
  setToBindPose() {
    for (let i = 0; i < this._localTransforms.length; i++) {
      const joint = this._skeleton._joints[i];
      // Set the localTransform to the inverted inverseBindPose, which is bindpose
      joint._inverseBindPose.invert(this._localTransforms[i]);

      if (joint._parentIndex !== Joint.NO_PARENT) {
        /// At this point we are in model space, so we need to remove our parent's transform (if we have one.)
        // We remove the parent's transform simply by multiplying by its inverse bind pose.
        const jointParent = this._skeleton._joints[joint._parentIndex];
        this._localTransforms[i].multiply(jointParent._inverseBindPose, this._localTransforms[i]);
      }
    }
    this.updateTransforms();
  }

  /**
   * Update the global and palette transforms of our posed joints based on the current local joint transforms.
   */
  updateTransforms() {
    const joints = this._skeleton._joints;
    for (let i = 0, l = joints.length; i < l; i++) {
      const parentIndex = joints[i]._parentIndex;
      if (parentIndex !== Joint.NO_PARENT) {
        // We have a parent, so take us from local->parent->world space by multiplying by parent's local->world
        this._globalTransforms[i].multiply(this._globalTransforms[parentIndex], this._localTransforms[i]);
      } else {
        // No parent so just set global to the local transform
        this._globalTransforms[i].copy(this._localTransforms[i]);
      }

      /*
       * At this point we have a local->world space transform for this joint, for skinning we multiply this by the
       * joint's inverse bind pose (joint->world space, inverted). This gives us a transform that can take a
       * vertex from bind pose (world space) to current pose (world space).
       */
      this._matrixPalette[i].mul2(
        this._globalTransforms[i].matrix,
        joints[i]._inverseBindPose.matrix,
      );
    }

    this.firePoseUpdated();
  }

  /*
   * Notify any registered PoseListeners that this pose has been "updated".
   */
  firePoseUpdated() {
    for (let i = this._poseListeners.length - 1; i >= 0; i--) {
      this._poseListeners[i].poseUpdated(this);
    }
  }

  clone() {
    return new SkeletonPose(this._skeleton);
  }
}

export default SkeletonPose;
